export type TourStepMode = 'coach' | 'navigate' | 'instruction' | 'outcome-check';

/** @deprecated Prefer `coach` — kept for older defs until each domain is rewritten. */
export type TourStepModeLegacy = TourStepMode | 'spotlight';

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/**
 * Gate primary CTA until condition met.
 * `outcome-check` mode without explicit await ≡ `org-complete`.
 */
export type TourAwait =
  | 'none'
  | 'org-complete'
  | { pathMatch: string }
  | { routePrefix: string }
  /** Element matching selector is in the DOM and painted (e.g. flyout item). */
  | { selectorVisible: string }
  /**
   * User activated the step’s pulsed control (click / keyboard).
   * Use when routePrefix would already be true (e.g. already on /catalog).
   */
  | { targetClicked: true }
  /**
   * User clicked an element matching this selector (capture).
   * Use when the pulsed control only reveals the real target (Inventaire → Produits).
   */
  | { clickSelector: string };

export function isTargetClickedAwait(awaitSpec: TourAwait): awaitSpec is { targetClicked: true } {
  return typeof awaitSpec === 'object' && awaitSpec !== null && 'targetClicked' in awaitSpec;
}

export function isSelectorVisibleAwait(
  awaitSpec: TourAwait,
): awaitSpec is { selectorVisible: string } {
  return typeof awaitSpec === 'object' && awaitSpec !== null && 'selectorVisible' in awaitSpec;
}

export function isClickSelectorAwait(awaitSpec: TourAwait): awaitSpec is { clickSelector: string } {
  return typeof awaitSpec === 'object' && awaitSpec !== null && 'clickSelector' in awaitSpec;
}

export type TourStepDef = {
  id: string;
  /** CSS selector, prefer [data-tour="…"] — omit for instruction/outcome-check modes */
  selector?: string;
  /** First visible match wins — for forked paths (e.g. create vs import). */
  selectors?: readonly string[];
  title: string;
  body: string;
  /** @deprecated Unused on tour card — kept for def compat. */
  goal?: string;
  /** Gate Terminer / auto-advance until met; auto-advance when met and not last step. */
  await?: TourAwait;
  /** Default `coach` (pulse + tip). `spotlight` accepted as alias for coach. */
  mode?: TourStepModeLegacy;
  /**
   * Optional assist navigate when already on wrong page.
   * Force-nav steps omit this — user must click the pulsed control.
   */
  route?: string;
  /** When true, `route` must match pathname exactly (no `/roles/id` under `/roles`). */
  routeExact?: boolean;
  placement?: TourPlacement;
  /**
   * Temporarily pin-open the pulsed NavCluster (Inventaire) so the user only
   * clicks the inner entry (e.g. Produits). Cleared when the step ends.
   */
  autoOpenNavCluster?: boolean;
  /** Pin-open Catalogue / Utilisateurs « Créer » menu (Un produit|compte / Importer). */
  autoOpenCreateMenu?: boolean;
};

export type TourDefinition = {
  id: string;
  flowId?: string;
  steps: TourStepDef[];
};

export type ActiveTour = {
  tourId: string;
  flowId: string | null;
  steps: TourStepDef[];
  index: number;
};

export function resolveStepAwait(step: TourStepDef): TourAwait {
  if (step.await != null) return step.await;
  if (step.mode === 'outcome-check') return 'org-complete';
  return 'none';
}

export function isAwaitNone(awaitSpec: TourAwait): boolean {
  return awaitSpec === 'none';
}

/** Step that pulses a real control and waits for the user (no free Suivant). */
export function isForceClickStep(step: TourStepDef): boolean {
  const awaitSpec = resolveStepAwait(step);
  if (isAwaitNone(awaitSpec)) return false;
  return Boolean(step.selector || step.selectors?.length);
}

export function resolveStepMode(step: TourStepDef): TourStepMode | 'spotlight' {
  return step.mode ?? 'coach';
}

/**
 * Steps that need ephemeral UI (open drawer / menu / wizard / entity detail)
 * are bad restore points after leave — rewind to the nearest durable coach target.
 */
export function isEphemeralResumeStep(step: TourStepDef): boolean {
  const mode = resolveStepMode(step);
  if (mode === 'instruction' || mode === 'outcome-check') return true;
  const sel = step.selector ?? '';
  if (sel.includes('wizard') || sel.includes('drawer') || sel.includes('modal')) return true;
  /* Detail panels only exist after opening a row — list deep-link cannot restore. */
  if (sel.includes('-detail-') || sel.includes('detail-packs') || sel.includes('detail-panel')) {
    return true;
  }
  if (
    step.selectors?.some(
      (s) =>
        s.includes('wizard') ||
        s.includes('drawer') ||
        s.includes('modal') ||
        s.includes('-detail-') ||
        s.includes('detail-packs') ||
        s.includes('detail-panel'),
    )
  ) {
    return true;
  }
  /* Menu item only exists while parent menu is open. */
  if (sel.includes('catalog-create-single') || sel.includes('catalog-create-import')) return true;
  if (step.selectors?.some((s) => s.includes('catalog-create-single') || s.includes('catalog-create-import'))) {
    return true;
  }
  return false;
}

/** Index to resume from `lastStepId`, rewinding past ephemeral wait steps. */
export function resolveResumeIndex(steps: readonly TourStepDef[], lastStepId: string | null): number {
  if (!steps.length) return 0;
  if (!lastStepId) return 0;
  let idx = steps.findIndex((s) => s.id === lastStepId);
  if (idx < 0) return 0;
  while (idx > 0 && isEphemeralResumeStep(steps[idx]!)) {
    idx -= 1;
  }
  if (isEphemeralResumeStep(steps[idx]!)) {
    const safe = steps.findIndex((s) => !isEphemeralResumeStep(s));
    return safe >= 0 ? safe : 0;
  }
  return idx;
}
